import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, AlertCircle, CheckCircle, Clock, Calendar, RefreshCw } from 'lucide-react';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Complaint {
  _id: string;
  bookingId?: {
    _id?: string;
    bookingId?: number;
  } | string;
  subject: string;
  description: string;
  isUrgent: boolean;
  status: 'pending' | 'under_review' | 'pending_manager' | 'resolved';
  resolution?: string;
  resolvedBy?: {
    name: string;
    email: string;
  };
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const MyComplaintsPage: React.FC = () => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedComplaint, setExpandedComplaint] = useState<string | null>(null);

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/complaints/my-complaints`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComplaints(response.data.data.complaints);
      setError('');
    } catch (err: any) {
      console.error('Error fetching complaints:', err);
      setError(err.response?.data?.message || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'under_review':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending_manager':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'under_review':
        return <AlertCircle className="h-4 w-4" />;
      case 'pending_manager':
        return <AlertTriangle className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getBookingId = (bookingId: any) => {
    if (!bookingId) return null;
    if (typeof bookingId === 'object') {
      return bookingId.bookingId || bookingId._id?.slice(-8);
    }
    return bookingId.slice(-8);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black">My Complaints</h1>
          <p className="text-gray-600 mt-2">Track the status of your submitted complaints</p>
        </div>

        {/* Refresh Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={fetchComplaints}
            disabled={loading}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Complaints List */}
        {complaints.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-black mb-2">No Complaints Found</h3>
            <p className="text-gray-600">You haven't submitted any complaints yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {complaints.map((complaint) => (
              <div
                key={complaint._id}
                className={`bg-white rounded-lg shadow-sm overflow-hidden ${
                  complaint.isUrgent ? 'border-2 border-red-500' : 'border border-gray-200'
                }`}
              >
                {/* Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {complaint.isUrgent && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full flex items-center">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            URGENT
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-black">{complaint.subject}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Submitted on {new Date(complaint.createdAt).toLocaleDateString()} at{' '}
                        {new Date(complaint.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-lg border-2 flex items-center space-x-2 ${getStatusColor(complaint.status)}`}>
                      {getStatusIcon(complaint.status)}
                      <span className="font-semibold text-sm">{formatStatus(complaint.status)}</span>
                    </div>
                  </div>

                  {/* Related Booking */}
                  {complaint.bookingId && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 border-l-4 border-gray-400">
                      <div className="flex items-center text-sm">
                        <Calendar className="h-4 w-4 text-black mr-2" />
                        <span className="font-semibold text-black mr-2">Related Booking:</span>
                        <span className="text-gray-800">ID: {getBookingId(complaint.bookingId) || 'N/A'}</span>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="mb-4">
                    <button
                      onClick={() => setExpandedComplaint(expandedComplaint === complaint._id ? null : complaint._id)}
                      className="text-sm font-medium text-black hover:text-gray-800"
                    >
                      {expandedComplaint === complaint._id ? 'Hide Details' : 'Show Details'}
                    </button>
                    
                    {expandedComplaint === complaint._id && (
                      <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 p-4">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{complaint.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Resolution */}
                  {complaint.status === 'resolved' && complaint.resolution && (
                    <div className="bg-green-50 border-l-4 border-green-400 p-4">
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-green-900 mb-1">✅ Resolution:</p>
                          <p className="text-sm text-gray-700">{complaint.resolution}</p>
                          {complaint.resolvedAt && (
                            <p className="text-xs text-gray-600 mt-2">
                              Resolved on {new Date(complaint.resolvedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Message */}
                  {complaint.status !== 'resolved' && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700">
                        {complaint.status === 'pending' && (
                          <>⏳ Your complaint is pending review. Our team will respond shortly.</>
                        )}
                        {complaint.status === 'under_review' && (
                          <>🔍 Your complaint is currently under review by our support team.</>
                        )}
                        {complaint.status === 'pending_manager' && (
                          <>📋 Your complaint has been escalated to management for further review.</>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-2">About Complaint Status:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Pending:</strong> Complaint received, awaiting review</li>
                <li><strong>Under Review:</strong> Support team is investigating your issue</li>
                <li><strong>Pending Manager:</strong> Escalated to management</li>
                <li><strong>Resolved:</strong> Issue has been addressed and closed</li>
              </ul>
              <p className="mt-3">
                💡 <strong>Tip:</strong> Our customer care executive will contact you via phone to discuss your complaint.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyComplaintsPage;

